import { LitElement, css, html } from "lit";
import { defineChromeElement } from "../chrome/ChromeElement";
import { fallbackLabels, type LabelTable, type Labels } from "../chrome/chromeLabels";
import { chromeControlStyles } from "../chrome/chromeStyles";
import { fitTexts } from "../chrome/fitText";
import type { FeatureHudPort } from "./FeatureHudPort";
import type { ScreenShape } from "@slot/presentation";

export const FEATURE_HUD_LABELS = { totalWin: ["feature_hud.total_win", "Total Win"], spinsLeft: ["feature_hud.spins_left", "Spins Left"] } as const satisfies LabelTable;
export type FeatureHudLabels = Labels<typeof FEATURE_HUD_LABELS>;

export const FEATURE_HUD_TAG = "slot-feature-hud";

/**
 * `<slot-feature-hud>`: the free-spins HUD — "Total Win" and "Spins Left" over their amounts in a
 * dark box, authored once per shape (`variant`: the box and the half-width columns' label boxes).
 * The bar places it and sets the variant; the presenter drives the port.
 */
export class FeatureHudElement extends LitElement implements FeatureHudPort {
  public static override properties = {
    variant: { type: String, reflect: true },
    labels: { attribute: false },
    shown: { type: Boolean, reflect: true },
    win: { state: true },
    spins: { state: true },
  };

  public declare variant: ScreenShape;
  public declare labels: FeatureHudLabels;
  public declare shown: boolean;
  private declare win: string;
  private declare spins: string;

  public constructor() {
    super();
    this.variant = "mobile";
    this.labels = fallbackLabels(FEATURE_HUD_LABELS);
    this.shown = false;
    this.win = "";
    this.spins = "";
  }

  public static override styles = [
    chromeControlStyles,
    css`
      /* The portrait box; the label rows sit at their offsets from the box's centre line (y-down). */
      :host { display: block; position: relative; width: 552.6px; height: 206px; background: rgba(25, 24, 24, 0.882); color: #fff; --title-width: 252.2px; --amount-width: 252.2px; --title-y: -33.52px; --amount-y: 54.98px; }
      :host([variant="desktop"]) { width: 689.7px; height: 170.7px; --title-width: 297.8px; --amount-width: 297.8px; --amount-y: 37.31px; }
      :host([variant="mobile_landscape"]) { width: 310.5px; height: 151.3px; --title-width: 134.9px; --amount-width: 138.2px; --amount-y: 27.63px; }
      :host(:not([shown])) { display: none; }
      .column { position: absolute; top: 0; bottom: 0; width: 50%; }
      .column.win { left: 0; }
      .column.spins { right: 0; }
      .row { position: absolute; left: 50%; transform: translate(-50%, -50%); display: flex; align-items: center; justify-content: center; text-align: center; overflow: hidden; }
      .title { top: calc(50% + var(--title-y)); width: var(--title-width); height: 64.51px; color: #fc8404; font-weight: 300; line-height: 1.05; white-space: normal; }
      .title [data-fit] { white-space: normal; }
      .win .title { font-size: 40px; }
      .spins .title { font-size: 36.9px; }
      .amount { top: calc(50% + var(--amount-y)); width: var(--amount-width); height: 50.4px; font-size: 50px; font-weight: 500; }
    `,
  ];

  public show(_featureId: string, accumulatedWinText: string, spinsLeftText: string): void {
    this.win = accumulatedWinText;
    this.spins = spinsLeftText;
    this.shown = true;
  }

  public hide(): void {
    this.shown = false;
  }

  public setAccumulatedWinText(text: string): void {
    this.win = text;
  }

  public setSpinsLeftText(text: string): void {
    this.spins = text;
  }

  protected override updated(): void {
    fitTexts(this.renderRoot);
  }

  protected override render() {
    const column = (title: string, amount: string, part: string) => html`
      <div class="row title shadow"><span data-fit>${title}</span></div>
      <div class="row amount shadow" data-part=${part}><span data-fit>${amount}</span></div>
    `;
    return html`
      <div class="column win">${column(this.labels.totalWin, this.win, "total-win")}</div>
      <div class="column spins">${column(this.labels.spinsLeft, this.spins, "spins-left")}</div>
    `;
  }
}

export function defineFeatureHudElement(): void {
  defineChromeElement(FEATURE_HUD_TAG, FeatureHudElement);
}
